import React, { useEffect, useRef } from "react";
import screenshot1 from "../assets/screenshots/Screenshot_1_optimized.webp";
import screenshot2 from "../assets/screenshots/Screenshot_2_optimized.webp";
import screenshot3 from "../assets/screenshots/Screenshot_3_optimized.webp";
import screenshot4 from "../assets/screenshots/Screenshot_4_optimized.webp";
import screenshot5 from "../assets/screenshots/Screenshot_5_optimized.webp";
import screenshot6 from "../assets/screenshots/Screenshot_6_optimized.webp";
import googlePlayBadge from "../assets/GetItOnGooglePlay_Badge_Web_color_English.png";
import appStoreBadge from "../assets/download-on-the-app-store.svg";

const Home = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    // Intersection Observer for scroll animations
    const defaultObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    const firstFeatureObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '0px 0px 140px 0px'
      }
    );

    observerRef.current = [defaultObserver, firstFeatureObserver];

    const firstFeature = document.querySelector('.feature-section:first-child');
    const defaultElements = document.querySelectorAll('.hero-section, .feature-section:not(:first-child)');
    defaultElements.forEach((el) => defaultObserver.observe(el));
    if (firstFeature) {
      firstFeatureObserver.observe(firstFeature);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.forEach((observer) => observer.disconnect());
      }
    };
  }, []);

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">Steps. Simplified.</h1>
          <p className="hero-subtitle">
            A minimalist step counter for everyday adventures. Simple, elegant, and battery-friendly.
          </p>
          <div className="hero-cta-group">
            <div className="hero-cta-row">
              <a
                href="https://play.google.com/store/apps/details?id=com.kjm.stepcounter"
                target="_blank"
                rel="noopener noreferrer"
                className="store-badge-link"
              >
                <img 
                  src={googlePlayBadge} 
                  alt="Get it on Google Play" 
                  className="google-play-badge hero-badge"
                />
              </a>
              <a
                href="https://apps.apple.com/app/id6779684228"
                target="_blank"
                rel="noopener noreferrer"
                className="store-badge-link"
              >
                <img
                  src={appStoreBadge}
                  alt="Download on the App Store"
                  className="app-store-badge hero-badge"
                />
              </a>
            </div>
            <a href="#features" className="hero-learn-more-link">
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-container">
        <div className="feature-section">
          <div className="feature-image">
            <img src={screenshot1} alt="Simple Step Counter Main Screen" />
          </div>
          <div className="feature-text">
            <h2>Simple & Elegant Design</h2>
            <p>
              Experience a clean, minimalist interface that puts your step count front and center. 
              No clutter, no distractions – just the information you need at a glance.
            </p>
          </div>
        </div>

        <div className="feature-section reverse">
          <div className="feature-image">
            <img src={screenshot2} alt="Activity history and statistics" />
          </div>
          <div className="feature-text">
            <h2>Complete History</h2>
            <p>
              Review your activity history and gain insights into your walking patterns.
              Track your streaks and see how consistent you’ve been over time.
            </p>
          </div>
        </div>

        <div className="feature-section">
          <div className="feature-image">
            <img src={screenshot3} alt="Customizable home screen widgets" />
          </div>
          <div className="feature-text">
            <h2>Accurate Step Tracking</h2>
            <p>
              Advanced algorithms ensure precise step counting throughout your day.
              Whether you're walking, running, or climbing stairs, Simple Step Counter keeps an accurate count.
            </p>
          </div>
        </div>

        <div className="feature-section reverse">
          <div className="feature-image">
            <img src={screenshot4} alt="Widget customization options" />
          </div>
          <div className="feature-text">
            <h2>Your Widget, Your Style</h2>
            <p>
              Fully customize your widget to match your look and preferences.
              Make it yours, exactly how you want it.
            </p>
          </div>
        </div>

        <div className="feature-section">
          <div className="feature-image">
            <img src={screenshot5} alt="Goal and measurement settings" />
          </div>
          <div className="feature-text">
            <h2>Set Your Goals</h2>
            <p>
              Customize your daily step goals to match your fitness level.
              Simple Step Counter helps you stay on track with visual progress indicators and streak tracking.
            </p>
          </div>
        </div>

        <div className="feature-section reverse">
          <div className="feature-image">
            <img src={screenshot6} alt="Settings and preferences" />
          </div>
          <div className="feature-text">
            <h2>Battery Friendly</h2>
            <p>
              Optimized for minimal battery consumption.
              Track your steps all day without worrying about draining your phone's battery.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Start Your Journey?</h2>
            <p>Download Simple Step Counter for iPhone or Android and start tracking your daily steps.</p>
          <div className="hero-cta-row">
            <a
              href="https://play.google.com/store/apps/details?id=com.kjm.stepcounter"
              target="_blank"
              rel="noopener noreferrer"
              className="store-badge-link google-play-badge-link"
            >
              <img
                src={googlePlayBadge}
                alt="Get it on Google Play"
                className="google-play-badge"
              />
            </a>
            <a
              href="https://apps.apple.com/app/id6779684228"
              target="_blank"
              rel="noopener noreferrer"
              className="store-badge-link"
            >
              <img
                src={appStoreBadge}
                alt="Download on the App Store"
                className="app-store-badge"
              />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
